(() => {
  if (window.__tanProductionDeliveryReady) {
    window.dispatchEvent(new CustomEvent('tan:production-delivery:init'));
    return;
  }
  window.__tanProductionDeliveryReady = true;

  const DAY = 86400000;
  const SUPPORT_EMAIL = 'support@theartneon.com';
  const copyCache = new WeakMap();
  const clean = (value) => String(value || '').replace(/\s+/g, ' ').trim();
  const readCopy = (root) => {
    if (!root) return {};
    if (copyCache.has(root)) return copyCache.get(root);
    let parsed = {};
    try {
      const value = JSON.parse(root.querySelector('[data-tan-deadline-copy]')?.textContent || '{}');
      if (value && typeof value === 'object' && !Array.isArray(value)) parsed = value;
    } catch (_) {
      parsed = {};
    }
    copyCache.set(root, parsed);
    return parsed;
  };
  const copyText = (copy, key, fallback = '') => {
    const value = copy?.[key];
    return typeof value === 'string' && value.trim() ? value : fallback;
  };
  const formatCopy = (template, values = {}) => String(template || '').replace(/\{([a-z_]+)\}/gi, (match, key) => (
    Object.prototype.hasOwnProperty.call(values, key) ? String(values[key]) : match
  ));
  const pluralCopy = (copy, key, count) => {
    let category = 'other';
    try {
      category = new Intl.PluralRules(document.documentElement.lang || 'en').select(Number(count)) === 'one' ? 'one' : 'other';
    } catch (_) {
      category = Number(count) === 1 ? 'one' : 'other';
    }
    return formatCopy(copyText(copy, `${key}_${category}`, copyText(copy, `${key}_other`)), { count });
  };
  const numeric = (value, fallback = 0) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  };
  const publish = (name, details = {}) => {
    const data = { event_version: '1.0', page_type: 'production_delivery', ...details };
    window.dispatchEvent(new CustomEvent('tan:lead-event', { detail: { name, data } }));
    try {
      window.Shopify?.analytics?.publish?.('the_art_neon:' + name, data);
    } catch (_) {
      // The planning experience remains usable when analytics is unavailable.
    }
  };
  const activeChoice = (root, group) => root.querySelector('[data-deadline-choice][data-choice-group="' + group + '"].is-active');
  const selectedLabel = (field) => clean(field?.selectedOptions?.[0]?.textContent);
  const localToday = () => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
  };
  const parseLocalDate = (value) => {
    const match = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!match) return null;
    const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
    return Number.isNaN(date.getTime()) ? null : date;
  };
  const addDays = (date, days) => new Date(date.getFullYear(), date.getMonth(), date.getDate() + Math.round(days));
  const inputDate = (date) => [date.getFullYear(), String(date.getMonth() + 1).padStart(2, '0'), String(date.getDate()).padStart(2, '0')].join('-');
  const formatDate = (date) => {
    try {
      return new Intl.DateTimeFormat(document.documentElement.lang || 'en', { day: 'numeric', month: 'short', year: 'numeric' }).format(date);
    } catch (_) {
      return inputDate(date);
    }
  };
  const deadlineBand = (days) => {
    if (!Number.isFinite(days)) return 'not_added';
    if (days < 0) return 'passed';
    if (days <= 7) return '0_7_days';
    if (days <= 13) return '8_13_days';
    if (days <= 20) return '14_20_days';
    if (days <= 27) return '21_27_days';
    if (days <= 41) return '28_41_days';
    return '42_plus_days';
  };
  const currentUtms = () => {
    const current = new URLSearchParams(window.location.search);
    const values = {};
    ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'].forEach((key) => {
      if (current.get(key)) values[key] = current.get(key);
    });
    return values;
  };
  const routeUrl = (base, details = {}) => {
    if (!base || base.startsWith('#') || base.startsWith('mailto:')) return base;
    try {
      const url = new URL(base, window.location.href);
      Object.entries({ ...currentUtms(), tan_source: 'production_delivery', ...details }).forEach(([key, value]) => {
        if (value) url.searchParams.set(key, value);
      });
      return url.toString();
    } catch (_) {
      return base;
    }
  };

  const initializePlanner = (root) => {
    if (!root || root.dataset.deadlinePlannerReady) return;
    root.dataset.deadlinePlannerReady = 'true';
    const copy = readCopy(root);

    const choices = Array.from(root.querySelectorAll('[data-deadline-choice]'));
    const serviceField = root.querySelector('[data-deadline-service]');
    const serviceContainer = serviceField?.closest('label');
    const readinessField = root.querySelector('[data-deadline-readiness]');
    const dateField = root.querySelector('[data-deadline-date]');
    const badge = root.querySelector('[data-deadline-badge]');
    const heading = root.querySelector('[data-deadline-heading]');
    const reason = root.querySelector('[data-deadline-reason]');
    const windowOutput = root.querySelector('[data-deadline-window]');
    const startOutput = root.querySelector('[data-deadline-start]');
    const availableOutput = root.querySelector('[data-deadline-available]');
    const allowanceOutput = root.querySelector('[data-deadline-allowance]');
    const guidanceOutput = root.querySelector('[data-deadline-guidance]');
    const primary = root.querySelector('[data-deadline-primary]');
    const copyButton = root.querySelector('[data-deadline-copy]');
    const briefOutput = root.querySelector('[data-tan-brief-output]');
    const today = localToday();
    if (dateField) dateField.min = inputDate(today);

    const config = {
      standard: {
        indoor: [numeric(root.dataset.standardIndoorMin, 14), numeric(root.dataset.standardIndoorMax, 21)],
        outdoor: [numeric(root.dataset.standardOutdoorMin, 14), numeric(root.dataset.standardOutdoorMax, 21)],
      },
      rush: {
        indoor: [numeric(root.dataset.rushIndoorMin, 10), numeric(root.dataset.rushIndoorMax, 12)],
        outdoor: [numeric(root.dataset.rushOutdoorMin, 10), numeric(root.dataset.rushOutdoorMax, 12)],
      },
      readiness: {
        ready: numeric(root.dataset.readyDays, 0),
        revisions: numeric(root.dataset.revisionDays, 0),
        design: numeric(root.dataset.designDays, 0),
      },
    };
    Object.values(config.standard).forEach((range) => range.sort((a, b) => a - b));
    Object.values(config.rush).forEach((range) => range.sort((a, b) => a - b));

    const state = () => ({
      routeChoice: activeChoice(root, 'delivery_route'),
      environmentChoice: activeChoice(root, 'delivery_environment'),
      regionChoice: activeChoice(root, 'delivery_region'),
      purposeChoice: activeChoice(root, 'delivery_purpose'),
      service: clean(serviceField?.value) || 'standard',
      readiness: clean(readinessField?.value) || 'ready',
      target: parseLocalDate(dateField?.value),
    });
    const choiceKey = (choice) => clean(choice?.dataset.choiceId);
    const choiceTitle = (choice) => clean(choice?.dataset.choiceTitle);

    const calculate = () => {
      const current = state();
      if (!current.routeChoice || !current.environmentChoice || !current.regionChoice || !current.purposeChoice) return null;
      const route = choiceKey(current.routeChoice);
      const environment = choiceKey(current.environmentChoice) === 'outdoor' ? 'outdoor' : 'indoor';
      const region = choiceKey(current.regionChoice);
      const purpose = choiceKey(current.purposeChoice);
      const isBulk = route === 'business';
      const effectiveService = isBulk ? 'bulk' : (current.service === 'rush' ? 'rush' : 'standard');
      const range = isBulk ? [30, 45] : config[effectiveService][environment];
      const rushRange = isBulk ? [30, 45] : config.rush[environment];
      const approvalDays = Math.max(0, numeric(current.routeChoice.dataset.routeAllowance) + numeric(config.readiness[current.readiness]));
      const bufferDays = Math.max(0, numeric(current.purposeChoice.dataset.bufferDays));
      const arrivalMin = addDays(today, approvalDays + range[0]);
      const arrivalMax = addDays(today, approvalDays + range[1]);
      const daysAvailable = current.target ? Math.ceil((current.target.getTime() - today.getTime()) / DAY) : NaN;
      const recommendedStart = current.target ? addDays(current.target, -(approvalDays + range[1] + bufferDays)) : null;
      const teamReview = current.regionChoice.dataset.teamReview === 'true' || (region === 'australia' && effectiveService !== 'rush');
      let status = teamReview ? 'review' : 'pending';
      if (current.target) {
        if (daysAvailable < 0) status = 'passed';
        else if (teamReview) status = 'review';
        else if (daysAvailable >= approvalDays + range[1] + bufferDays) status = 'comfortable';
        else if (daysAvailable >= approvalDays + range[0]) status = 'tight';
        else status = 'review';
      }
      const rushComfortable = current.target && !teamReview && daysAvailable >= approvalDays + rushRange[1] + bufferDays;
      const rushPossible = current.target && !teamReview && daysAvailable >= approvalDays + rushRange[0];
      const guidance = [];
      if (teamReview) {
        guidance.push(formatCopy(copyText(copy, 'guidance_team_review'), { email: SUPPORT_EMAIL }));
      } else {
        if (region === 'australia') guidance.push(formatCopy(copyText(copy, 'guidance_australia'), { email: SUPPORT_EMAIL }));
        if (!current.target) guidance.push(copyText(copy, 'guidance_add_date'));
        if (status === 'comfortable') guidance.push(copyText(copy, 'guidance_comfortable'));
        if (status === 'tight') guidance.push(copyText(copy, 'guidance_tight'));
        if (status === 'review') guidance.push(copyText(copy, 'guidance_review'));
        if (status === 'passed') guidance.push(copyText(copy, 'guidance_passed'));
        if (!isBulk && current.service === 'undecided' && status !== 'comfortable') {
          if (rushComfortable) guidance.push(copyText(copy, 'guidance_compare_express'));
          else if (rushPossible) guidance.push(copyText(copy, 'guidance_express_partial'));
          else guidance.push(copyText(copy, 'guidance_express_unavailable'));
        }
        if (!isBulk && current.service === 'rush') guidance.push(copyText(copy, 'guidance_express'));
        if (isBulk) guidance.push(formatCopy(copyText(copy, 'guidance_bulk'), { min: range[0], max: range[1] }));
        else if (current.readiness !== 'ready') guidance.push(copyText(copy, 'guidance_approval'));
        if (['event', 'opening'].includes(purpose)) guidance.push(copyText(copy, 'guidance_fixed_date'));
        guidance.push(copyText(copy, 'guidance_carrier'));
      }
      return {
        ...current,
        route,
        environment,
        region,
        purpose,
        isBulk,
        effectiveService,
        range,
        rushRange,
        approvalDays,
        bufferDays,
        arrivalMin,
        arrivalMax,
        daysAvailable,
        recommendedStart,
        teamReview,
        status,
        rushComfortable,
        rushPossible,
        guidance: [...new Set(guidance)],
      };
    };

    const statusLabel = (status) => clean(root.dataset[status + 'Label']) || clean(root.dataset.reviewLabel);
    const resultHeading = (result) => {
      if (result.teamReview) return copyText(copy, 'heading_team_review');
      if (result.isBulk) return formatCopy(copyText(copy, 'heading_bulk'), { min: result.range[0], max: result.range[1] });
      if (result.status === 'pending') return copyText(copy, 'heading_pending');
      if (result.status === 'comfortable') return copyText(copy, 'heading_comfortable');
      if (result.status === 'tight') return copyText(copy, result.service === 'rush' ? 'heading_tight_rush' : 'heading_tight_standard');
      if (result.status === 'passed') return copyText(copy, 'heading_passed');
      return copyText(copy, 'heading_review');
    };
    const resultReason = (result) => {
      if (result.teamReview) {
        return [choiceTitle(result.routeChoice), choiceTitle(result.environmentChoice), choiceTitle(result.regionChoice)].filter(Boolean).join(copyText(copy, 'item_separator'));
      }
      const service = result.isBulk
        ? formatCopy(copyText(copy, 'reason_bulk_schedule'), { min: result.range[0], max: result.range[1] })
        : (result.service === 'undecided' ? copyText(copy, 'reason_standard_baseline') : selectedLabel(serviceField));
      return [choiceTitle(result.routeChoice), choiceTitle(result.environmentChoice), choiceTitle(result.regionChoice), service].filter(Boolean).join(copyText(copy, 'item_separator'));
    };
    const targetFor = (result) => {
      if (result.teamReview) return root.dataset.supportLink;
      if (['review', 'passed'].includes(result.status)) {
        return ['logo', 'business'].includes(result.route) ? root.dataset.quoteLink : root.dataset.supportLink;
      }
      return result.routeChoice.dataset.ctaLink || root.dataset.quoteLink || root.dataset.supportLink;
    };
    const ctaLabel = (result) => {
      if (result.teamReview) return formatCopy(copyText(copy, 'cta_email_support'), { email: SUPPORT_EMAIL });
      if (['review', 'passed'].includes(result.status)) return copyText(copy, 'cta_review');
      if (result.status === 'tight' && result.service !== 'rush') return copyText(copy, 'cta_flag');
      return clean(result.routeChoice.dataset.ctaLabel) || copyText(copy, 'cta_continue');
    };

    const updateBrief = (result) => {
      if (!briefOutput || !result) return;
      const contactSupport = formatCopy(copyText(copy, 'contact_support'), { email: SUPPORT_EMAIL });
      const estimatedArrival = result.teamReview
        ? contactSupport
        : formatCopy(copyText(copy, 'date_range'), { start: formatDate(result.arrivalMin), end: formatDate(result.arrivalMax) });
      const availableTime = result.teamReview
        ? copyText(copy, 'route_timing_confirmation')
        : (Number.isFinite(result.daysAvailable) ? pluralCopy(copy, 'calendar_days', result.daysAvailable) : copyText(copy, 'target_date_not_added'));
      const projectBuffer = result.teamReview
        ? copyText(copy, 'set_after_route_confirmation')
        : pluralCopy(copy, 'days', result.bufferDays);
      const recommendedStart = result.teamReview
        ? contactSupport
        : (result.recommendedStart ? formatDate(result.recommendedStart) : copyText(copy, 'add_a_target_date'));
      briefOutput.value = [
        copyText(copy, 'brief_title'),
        formatCopy(copyText(copy, 'line_planning_status'), { value: statusLabel(result.status) }),
        formatCopy(copyText(copy, 'line_required_date'), { value: result.target ? inputDate(result.target) : copyText(copy, 'not_added_yet') }),
        formatCopy(copyText(copy, 'line_ordering_route'), { value: choiceTitle(result.routeChoice) }),
        formatCopy(copyText(copy, 'line_environment'), { value: choiceTitle(result.environmentChoice) }),
        formatCopy(copyText(copy, 'line_destination'), { value: choiceTitle(result.regionChoice) }),
        formatCopy(copyText(copy, 'line_date_purpose'), { value: choiceTitle(result.purposeChoice) }),
        formatCopy(copyText(copy, 'line_service'), {
          value: result.isBulk
            ? formatCopy(copyText(copy, 'brief_bulk_schedule'), { min: result.range[0], max: result.range[1] })
            : selectedLabel(serviceField),
        }),
        formatCopy(copyText(copy, 'line_readiness'), { value: selectedLabel(readinessField) }),
        formatCopy(copyText(copy, 'line_arrival'), { value: estimatedArrival }),
        formatCopy(copyText(copy, 'line_available'), { value: availableTime }),
        formatCopy(copyText(copy, 'line_buffer'), { value: projectBuffer }),
        formatCopy(copyText(copy, 'line_start'), { value: recommendedStart }),
        '',
        copyText(copy, 'guidance_heading'),
        ...result.guidance.map((item) => formatCopy(copyText(copy, 'guidance_item'), { value: item })),
        '',
        copyText(copy, 'brief_boundary'),
      ].join('\n');
    };
    const renderList = (element, values) => {
      if (!element) return;
      element.replaceChildren(...values.map((value) => {
        const item = document.createElement('li');
        item.textContent = value;
        return item;
      }));
    };
    const update = () => {
      const result = calculate();
      if (!result) return;
      root.classList.toggle('is-bulk-route', result.isBulk);
      if (serviceContainer) serviceContainer.style.display = result.isBulk ? 'none' : '';
      if (serviceField) serviceField.disabled = result.isBulk;
      if (badge) {
        badge.textContent = statusLabel(result.status);
        badge.dataset.status = result.status;
      }
      if (heading) heading.textContent = resultHeading(result);
      if (reason) reason.textContent = resultReason(result);
      if (windowOutput) {
        windowOutput.textContent = result.teamReview
          ? copyText(copy, 'confirm_by_email')
          : formatCopy(copyText(copy, 'date_range'), { start: formatDate(result.arrivalMin), end: formatDate(result.arrivalMax) });
      }
      if (startOutput) {
        startOutput.textContent = result.teamReview
          ? copyText(copy, 'set_after_route_review')
          : (result.recommendedStart ? formatDate(result.recommendedStart) : copyText(copy, 'add_target_date'));
      }
      if (availableOutput) {
        availableOutput.textContent = result.teamReview
          ? copyText(copy, 'not_confirmed_online')
          : (Number.isFinite(result.daysAvailable) ? pluralCopy(copy, 'calendar_days', result.daysAvailable) : copyText(copy, 'add_target_date'));
      }
      if (allowanceOutput) {
        allowanceOutput.textContent = result.teamReview
          ? copyText(copy, 'set_after_route_review')
          : pluralCopy(copy, 'days', result.bufferDays);
      }
      renderList(guidanceOutput, result.guidance);
      if (primary) {
        primary.textContent = ctaLabel(result);
        const base = targetFor(result);
        primary.hidden = !base;
        primary.href = routeUrl(base, {
          tan_delivery_route: result.route,
          tan_delivery_service: result.effectiveService,
          tan_delivery_status: result.status,
          tan_deadline_band: deadlineBand(result.daysAvailable),
        });
      }
      updateBrief(result);
      root.__tanDeadlineResult = result;
    };

    choices.forEach((choice) => {
      choice.addEventListener('click', () => {
        choices.filter((candidate) => candidate.dataset.choiceGroup === choice.dataset.choiceGroup).forEach((candidate) => {
          const selected = candidate === choice;
          candidate.classList.toggle('is-active', selected);
          candidate.setAttribute('aria-pressed', selected ? 'true' : 'false');
        });
        update();
      });
    });
    [serviceField, readinessField, dateField].filter(Boolean).forEach((field) => {
      field.addEventListener('input', update);
      field.addEventListener('change', update);
    });
    primary?.addEventListener('click', () => {
      const result = root.__tanDeadlineResult || calculate();
      if (!result) return;
      publish('delivery_plan_next_step', {
        route: result.route,
        environment: result.environment,
        destination_class: result.region,
        date_purpose: result.purpose,
        service: result.effectiveService,
        design_readiness: result.readiness,
        confidence_status: result.status,
        deadline_band: deadlineBand(result.daysAvailable),
        has_target_date: Boolean(result.target),
      });
    });
    copyButton?.addEventListener('click', async () => {
      const result = root.__tanDeadlineResult || calculate();
      updateBrief(result);
      const summary = briefOutput?.value || '';
      if (!summary) return;
      try {
        await navigator.clipboard.writeText(summary);
        const original = copyButton.textContent;
        copyButton.textContent = copyButton.dataset.copiedLabel || copyText(copy, 'copied', original);
        window.setTimeout(() => { copyButton.textContent = original; }, 1800);
      } catch (_) {
        briefOutput.hidden = false;
        briefOutput.removeAttribute('aria-hidden');
        briefOutput.focus();
        briefOutput.select();
      }
      if (result) publish('delivery_summary_copied', { service: result.service, confidence_status: result.status, deadline_band: deadlineBand(result.daysAvailable) });
    });
    update();
  };

  const initializeChecklist = (root) => {
    if (!root || root.dataset.deliveryChecklistReady) return;
    root.dataset.deliveryChecklistReady = 'true';
    const checks = Array.from(root.querySelectorAll('[data-delivery-check]'));
    const output = root.querySelector('[data-delivery-check-output]');
    const bar = root.querySelector('[data-delivery-check-progress]');
    const milestones = new Set();
    const update = () => {
      const count = checks.filter((check) => check.checked).length;
      const percent = checks.length ? Math.round((count / checks.length) * 100) : 0;
      if (output) output.textContent = count + ' / ' + checks.length;
      if (bar) bar.style.width = percent + '%';
      const milestone = percent === 100 ? 'complete' : percent >= 50 ? 'half' : '';
      if (milestone && !milestones.has(milestone)) {
        milestones.add(milestone);
        publish('delivery_checklist_progress', { milestone, total_checks: checks.length });
      }
    };
    checks.forEach((check) => check.addEventListener('change', update));
    update();
  };

  const initializeFaq = (root) => {
    if (!root || root.dataset.deliveryFaqReady) return;
    root.dataset.deliveryFaqReady = 'true';
    Array.from(root.querySelectorAll('details')).forEach((detail, index) => {
      detail.addEventListener('toggle', () => {
        if (!detail.open || detail.dataset.deliveryFaqTracked) return;
        detail.dataset.deliveryFaqTracked = 'true';
        publish('delivery_faq_opened', { question_position: index + 1 });
      });
    });
  };

  const initializeLinks = () => {
    document.querySelectorAll('[data-delivery-tracking-link]').forEach((link) => {
      if (link.dataset.deliveryTrackingReady) return;
      link.dataset.deliveryTrackingReady = 'true';
      link.addEventListener('click', () => publish('delivery_tracking_clicked', { route: 'track_order' }));
    });
    document.querySelectorAll('[data-delivery-support-link]').forEach((link) => {
      if (link.dataset.deliverySupportReady) return;
      link.dataset.deliverySupportReady = 'true';
      link.addEventListener('click', () => publish('delivery_support_clicked', { route: clean(link.dataset.deliverySupportLink) || 'support' }));
    });
  };

  const boot = () => {
    document.querySelectorAll('[data-tan-deadline-planner]').forEach(initializePlanner);
    document.querySelectorAll('[data-tan-delivery-checklist]').forEach(initializeChecklist);
    document.querySelectorAll('.tan-faq').forEach(initializeFaq);
    initializeLinks();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
  window.addEventListener('tan:production-delivery:init', boot);
  document.addEventListener('shopify:section:load', boot);
})();
